'use client';

import { cn } from '@/lib/utils';
import {
  FileText,
  Send,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ArrowRight,
} from 'lucide-react';

export type POStatus = 'draft' | 'pending_approval' | 'approved' | 'sent' | 'ordered' | 'partial' | 'received' | 'cancelled';

interface POStatusBadgeProps {
  status: POStatus;
  showIcon?: boolean;
  className?: string;
}

const statusConfig: Record<POStatus, {
  variant: 'default' | 'warning' | 'info' | 'success' | 'error' | 'secondary';
  label: string;
  icon: typeof FileText;
  bgColor: string;
  textColor: string;
  borderColor: string;
}> = {
  draft: {
    variant: 'secondary',
    label: 'Draft',
    icon: FileText,
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-300',
  },
  pending_approval: {
    variant: 'warning',
    label: 'Pending Approval',
    icon: Clock,
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-300',
  },
  approved: {
    variant: 'info',
    label: 'Approved',
    icon: CheckCircle,
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-300',
  },
  sent: {
    variant: 'info',
    label: 'Sent',
    icon: Send,
    bgColor: 'bg-cyan-100',
    textColor: 'text-cyan-700',
    borderColor: 'border-cyan-300',
  },
  ordered: {
    variant: 'info',
    label: 'Ordered',
    icon: Send,
    bgColor: 'bg-cyan-100',
    textColor: 'text-cyan-700',
    borderColor: 'border-cyan-300',
  },
  partial: {
    variant: 'warning',
    label: 'Partial',
    icon: Package,
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-300',
  },
  received: {
    variant: 'success',
    label: 'Received',
    icon: CheckCircle,
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    borderColor: 'border-green-300',
  },
  cancelled: {
    variant: 'error',
    label: 'Cancelled',
    icon: XCircle,
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    borderColor: 'border-red-300',
  },
};

const statusFlow: POStatus[] = [
  'draft',
  'pending_approval',
  'approved',
  'sent',
  'ordered',
  'partial',
  'received',
];

export function POStatusBadge({ status, showIcon = true, className }: POStatusBadgeProps) {
  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border',
        config.bgColor,
        config.textColor,
        config.borderColor,
        className
      )}
    >
      {showIcon && <Icon className="h-3.5 w-3.5" />}
      {config.label}
    </span>
  );
}

export function POStatusTimeline({ currentStatus }: { currentStatus: POStatus }) {
  const currentIndex = statusFlow.indexOf(currentStatus);

  return (
    <div className="flex items-center gap-1">
      {statusFlow.map((status, index) => {
        const config = statusConfig[status];
        const Icon = config.icon;
        const isActive = index <= currentIndex;
        const isCurrent = status === currentStatus;
        const isPast = index < currentIndex;

        return (
          <div key={status} className="flex items-center">
            {index > 0 && (
              <ArrowRight
                className={cn(
                  'h-4 w-4 mx-1',
                  isPast ? 'text-green-600' : 'text-gray-300'
                )}
              />
            )}
            <div
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors',
                isActive ? config.bgColor : 'bg-gray-100',
                isActive ? config.textColor : 'text-gray-500',
                isCurrent && 'ring-2 ring-offset-1 ring-primary'
              )}
            >
              <Icon className="h-3 w-3" />
              <span>{config.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function getNextStatuses(currentStatus: POStatus): POStatus[] {
  const transitions: Record<POStatus, POStatus[]> = {
    draft: ['pending_approval', 'approved', 'cancelled'],
    pending_approval: ['approved', 'draft', 'cancelled'],
    approved: ['sent', 'ordered', 'cancelled'],
    sent: ['ordered', 'cancelled'],
    ordered: ['partial', 'received', 'cancelled'],
    partial: ['partial', 'received', 'cancelled'],
    received: [],
    cancelled: ['draft'],
  };
  return transitions[currentStatus] || [];
}

export function canTransitionTo(currentStatus: POStatus, targetStatus: POStatus): boolean {
  return getNextStatuses(currentStatus).includes(targetStatus);
}

export function getStatusTransitionLabel(from: POStatus, to: POStatus): string {
  const labels: Record<string, string> = {
    'draft-pending_approval': 'Submit for Approval',
    'draft-approved': 'Approve',
    'draft-cancelled': 'Cancel',
    'pending_approval-approved': 'Approve',
    'pending_approval-draft': 'Return to Draft',
    'pending_approval-cancelled': 'Cancel',
    'approved-sent': 'Mark as Sent',
    'approved-ordered': 'Mark as Ordered',
    'approved-cancelled': 'Cancel',
    'sent-ordered': 'Confirm Order',
    'sent-cancelled': 'Cancel',
    'ordered-partial': 'Partial Receive',
    'ordered-received': 'Full Receive',
    'ordered-cancelled': 'Cancel',
    'partial-partial': 'Continue Receiving',
    'partial-received': 'Complete Receiving',
    'partial-cancelled': 'Cancel',
    'cancelled-draft': 'Reopen',
  };
  return labels[`${from}-${to}`] || `Change to ${to}`;
}
